"use client"

import type React from "react"
import { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { cn } from "@/lib/utils"

interface ScrollableItem {
  id: string
  label: string
  image?: string
  displayText?: string
}

export type CameraControlSelection = {
  cameraId: string
  lensId: string
  focalId: string
  apertureId: string
}

export type CameraControlApplyPayload = {
  selection: CameraControlSelection
  systemPromptObject: {
    "system prompt": {
      mode: string
      shotPreset: {
        camera: string
        lens: string
        focal: string
        "f-stop": string
      }
    }
  }
  nodePrompt: string
}

type CameraControlProps = {
  value?: Partial<CameraControlSelection>
  onApply?: (payload: CameraControlApplyPayload) => void
}

type CameraPreset = {
  name: string
  selection: CameraControlSelection
  updatedAt: number
}

const CAMERA_PRESETS_STORAGE_KEY = "flowmuse.image.cameraControl.presets.v1"
const DEFAULT_PRESETS: CameraPreset[] = [
  {
    name: "预设 1",
    selection: { cameraId: "red-v-raptor", lensId: "panavision-c", focalId: "24mm", apertureId: "f1.4" },
    updatedAt: 0,
  },
  {
    name: "预设 2",
    selection: { cameraId: "arri-alexa-35", lensId: "cooke-s4", focalId: "35mm", apertureId: "f4" },
    updatedAt: 0,
  },
  {
    name: "预设 3",
    selection: { cameraId: "imax-film", lensId: "arri-signature", focalId: "50mm", apertureId: "f11" },
    updatedAt: 0,
  },
  {
    name: "预设 4",
    selection: { cameraId: "sony-venice", lensId: "zeiss-ultra", focalId: "75mm", apertureId: "f4" },
    updatedAt: 0,
  },
]

function safeParsePresets(raw: string | null): CameraPreset[] | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null
    const list = parsed
      .map((p: any, idx: number) => {
        const sel = (p as any)?.selection || {}
        const selection: CameraControlSelection = {
          cameraId: String(sel.cameraId || ""),
          lensId: String(sel.lensId || ""),
          focalId: String(sel.focalId || ""),
          apertureId: String(sel.apertureId || ""),
        }
        if (!selection.cameraId || !selection.lensId || !selection.focalId || !selection.apertureId) return null
        return {
          name: typeof (p as any)?.name === "string" && (p as any).name.trim().length ? (p as any).name.trim() : `预设 ${idx + 1}`,
          selection,
          updatedAt: Number.isFinite((p as any)?.updatedAt) ? Number((p as any).updatedAt) : 0,
        } satisfies CameraPreset
      })
      .filter(Boolean) as CameraPreset[]
    if (list.length !== 4) return null
    return list
  } catch {
    return null
  }
}

const cameraData: ScrollableItem[] = [
  { id: "arri-alexa-35", label: "ARRI Alexa 35", image: "/plugins/camera/Arri-Alexa-35.webp" },
  { id: "red-v-raptor", label: "RED V-Raptor", image: "/plugins/camera/Red-V-Raptor.webp" },
  { id: "arriflex-16sr", label: "Arriflex 16SR", image: "/plugins/camera/Arriflex-16SR.webp" },
  { id: "panavision-dxl2", label: "Panavision DXL2", image: "/plugins/camera/Panavision-Millennium-DXL2.webp" },
  { id: "imax-film", label: "IMAX Film Camera", image: "/plugins/camera/IMAX-Film-Camera.webp" },
  { id: "sony-venice", label: "Sony Venice", image: "/plugins/camera/Sony-Venice.webp" },
]

const lensData: ScrollableItem[] = [
  { id: "cooke-s4", label: "Cooke S4/i", image: "/plugins/camera/Cooke-S4.webp" },
  { id: "hawk-v-lite", label: "Hawk V-Lite", image: "/plugins/camera/Hawk-V-Lite.webp" },
  { id: "jdc-xtal", label: "JDC Xtal Xpress", image: "/plugins/camera/JDC-Xtal-Xpress.webp" },
  { id: "helios", label: "Helios", image: "/plugins/camera/Helios.webp" },
  { id: "lensbaby", label: "Lensbaby Edge 80", image: "/plugins/camera/Lensbaby.webp" },
  { id: "petzval", label: "Petzval 85 Art", image: "/plugins/camera/Petzval.webp" },
  { id: "panavision-c", label: "Panavision C-Series", image: "/plugins/camera/Panavision-C-Series.webp" },
  { id: "arri-signature", label: "ARRI Signature Prime", image: "/plugins/camera/ARRI-Signature-Prime.webp" },
  { id: "zeiss-ultra", label: "Zeiss Ultra Prime", image: "/plugins/camera/Zeiss-Ultra-Prime.webp" },
  { id: "canon-k35", label: "Canon K-35", image: "/plugins/camera/Canon-K-35.webp" },
  { id: "laowa-macro", label: "Laowa Macro", image: "/plugins/camera/Laowa-Macro.webp" },
]

const focalLengthData: ScrollableItem[] = [
  { id: "8mm", label: "8mm", displayText: "8" },
  { id: "14mm", label: "14mm", displayText: "14" },
  { id: "24mm", label: "24mm", displayText: "24" },
  { id: "35mm", label: "35mm", displayText: "35" },
  { id: "50mm", label: "50mm", displayText: "50" },
  { id: "75mm", label: "75mm", displayText: "75" },
  { id: "125mm", label: "125mm", displayText: "125" },
]

const apertureData: ScrollableItem[] = [
  { id: "f1.4", label: "f/1.4", displayText: "1.4", image: "/plugins/camera/f-1.4.webp" },
  { id: "f4", label: "f/4.0", displayText: "4.0", image: "/plugins/camera/f-4.webp" },
  { id: "f11", label: "f/11", displayText: "11", image: "/plugins/camera/f-11.webp" },
]

function labelForId(data: ScrollableItem[], id: string | undefined): string {
  if (!id) return ""
  const found = data.find((x) => x.id === id)
  return found?.label ?? id
}

export function formatCameraControlSummary(value?: Partial<CameraControlSelection> | null): {
  configured: boolean
  text: string
} {
  const cameraId = value?.cameraId
  const lensId = value?.lensId
  const focalId = value?.focalId
  const apertureId = value?.apertureId
  const configured = Boolean(cameraId && lensId && focalId && apertureId)
  if (!configured) return { configured: false, text: "未配置" }
  const camera = labelForId(cameraData, cameraId)
  const lens = labelForId(lensData, lensId)
  const focal = labelForId(focalLengthData, focalId)
  const aperture = labelForId(apertureData, apertureId)
  return {
    configured: true,
    text: [camera, lens, focal, aperture].filter(Boolean).join(" · "),
  }
}

const CAMERA_SYSTEM_PROMPT_MODE =
  "simulate a photorealistic image as if physically captured by the specified cinema camera and lens, strictly following the optical perspective, field of view, depth of field, distortion, contrast roll off, color response, and cinematic characteristics implied by the shotPreset"

export function buildCameraControlApplyPayloadFromSelection(selection: CameraControlSelection): CameraControlApplyPayload {
  const cameraLabel = labelForId(cameraData, selection.cameraId)
  const lensLabel = labelForId(lensData, selection.lensId)
  const focalLabel = labelForId(focalLengthData, selection.focalId)
  const apertureLabel = labelForId(apertureData, selection.apertureId)

  const systemPromptObject: CameraControlApplyPayload["systemPromptObject"] = {
    "system prompt": {
      mode: CAMERA_SYSTEM_PROMPT_MODE,
      shotPreset: {
        camera: cameraLabel,
        lens: lensLabel,
        focal: focalLabel,
        "f-stop": apertureLabel,
      },
    },
  }

  return {
    selection,
    systemPromptObject,
    nodePrompt: JSON.stringify(systemPromptObject, null, 2),
  }
}

const ITEM_HEIGHT = 72
const VISIBLE_HEIGHT = 180
const TRANSITION_DURATION = 250
const DRAG_THRESHOLD = 3
const VELOCITY_THRESHOLD = 0.5
const DECELERATION = 0.95

interface ScrollableSelectorProps {
  data: ScrollableItem[]
  selectedIndex: number
  onSelectedIndexChange: (index: number) => void
  label?: string
}

function ScrollableSelector({ data, selectedIndex, onSelectedIndexChange, label }: ScrollableSelectorProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [scrollOffset, setScrollOffset] = useState(0)

  const dragStartRef = useRef({ y: 0, time: 0 })
  const lastMoveRef = useRef({ y: 0, time: 0 })
  const hasDraggedRef = useRef(false)
  const velocityRef = useRef(0)
  const momentumRef = useRef<number | null>(null)

  const centerOffset = useMemo(() => (VISIBLE_HEIGHT - ITEM_HEIGHT) / 2, [])
  const maxOffset = useMemo(() => (data.length - 1) * ITEM_HEIGHT, [data.length])

  useEffect(() => {
    if (!isDragging) {
      setScrollOffset(selectedIndex * ITEM_HEIGHT)
    }
  }, [selectedIndex, isDragging])

  useEffect(() => {
    return () => {
      if (momentumRef.current) {
        cancelAnimationFrame(momentumRef.current)
      }
    }
  }, [])

  const snapToNearest = useCallback(
    (offset: number) => {
      const newIndex = Math.round(offset / ITEM_HEIGHT)
      const clampedIndex = Math.max(0, Math.min(newIndex, data.length - 1))
      onSelectedIndexChange(clampedIndex)
    },
    [data.length, onSelectedIndexChange],
  )

  const startMomentum = useCallback(
    (velocity: number) => {
      let currentVelocity = velocity
      let currentOffset = scrollOffset

      const animate = () => {
        currentVelocity *= DECELERATION
        currentOffset += currentVelocity

        // Clamp offset
        currentOffset = Math.max(0, Math.min(currentOffset, maxOffset))

        setScrollOffset(currentOffset)

        if (Math.abs(currentVelocity) > VELOCITY_THRESHOLD && currentOffset > 0 && currentOffset < maxOffset) {
          momentumRef.current = requestAnimationFrame(animate)
        } else {
          snapToNearest(currentOffset)
          momentumRef.current = null
        }
      }

      momentumRef.current = requestAnimationFrame(animate)
    },
    [scrollOffset, maxOffset, snapToNearest],
  )

  const handlePrevious = useCallback(() => {
    if (selectedIndex > 0) {
      onSelectedIndexChange(selectedIndex - 1)
    }
  }, [selectedIndex, onSelectedIndexChange])

  const handleNext = useCallback(() => {
    if (selectedIndex < data.length - 1) {
      onSelectedIndexChange(selectedIndex + 1)
    }
  }, [selectedIndex, data.length, onSelectedIndexChange])

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // Cancel any ongoing momentum
    if (momentumRef.current) {
      cancelAnimationFrame(momentumRef.current)
      momentumRef.current = null
    }

    setIsDragging(true)
    hasDraggedRef.current = false
    const now = Date.now()
    dragStartRef.current = { y: e.clientY, time: now }
    lastMoveRef.current = { y: e.clientY, time: now }
    velocityRef.current = 0

    // Capture pointer for better tracking
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }, [])

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return

      const now = Date.now()
      const deltaY = lastMoveRef.current.y - e.clientY
      const deltaTime = now - lastMoveRef.current.time

      // Calculate velocity
      if (deltaTime > 0) {
        velocityRef.current = (deltaY / deltaTime) * 16 // Normalize to ~60fps
      }

      if (Math.abs(e.clientY - dragStartRef.current.y) > DRAG_THRESHOLD) {
        hasDraggedRef.current = true
      }

      const newOffset = Math.max(0, Math.min(scrollOffset + deltaY, maxOffset))
      setScrollOffset(newOffset)
      lastMoveRef.current = { y: e.clientY, time: now }
    },
    [isDragging, scrollOffset, maxOffset],
  )

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return
      ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
      setIsDragging(false)

      // Apply momentum if velocity is significant
      if (Math.abs(velocityRef.current) > VELOCITY_THRESHOLD * 2) {
        startMomentum(velocityRef.current)
      } else {
        snapToNearest(scrollOffset)
      }
    },
    [isDragging, scrollOffset, snapToNearest, startMomentum],
  )

  const handleItemClick = useCallback(
    (index: number) => {
      if (!hasDraggedRef.current) {
        onSelectedIndexChange(index)
      }
    },
    [onSelectedIndexChange],
  )

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault()
      e.stopPropagation()

      // Cancel any ongoing momentum
      if (momentumRef.current) {
        cancelAnimationFrame(momentumRef.current)
        momentumRef.current = null
      }

      const direction = e.deltaY > 0 ? 1 : -1
      const newIndex = Math.max(0, Math.min(selectedIndex + direction, data.length - 1))
      onSelectedIndexChange(newIndex)
    },
    [selectedIndex, data.length, onSelectedIndexChange],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault()
        e.stopPropagation()
        handlePrevious()
      } else if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault()
        e.stopPropagation()
        handleNext()
      }
    },
    [handlePrevious, handleNext],
  )

  const renderedItems = useMemo(() => {
    return data.map((item, index) => {
      const distance = Math.abs(index * ITEM_HEIGHT - scrollOffset) / ITEM_HEIGHT
      const isSelected = index === selectedIndex
      const opacity = Math.max(0.25, 1 - distance * 0.4)
      const scale = Math.max(0.65, 1 - distance * 0.18)

      return (
        <div
          key={item.id}
          role="option"
          aria-selected={isSelected}
          className="flex items-center justify-center shrink-0 cursor-pointer"
          style={{
            width: 72,
            height: ITEM_HEIGHT,
            opacity,
            transform: `scale(${scale})`,
            transition: isDragging ? "none" : `all ${TRANSITION_DURATION}ms cubic-bezier(0.33, 1, 0.68, 1)`,
          }}
          onClick={() => handleItemClick(index)}
        >
          <div className="relative flex items-center justify-center h-full w-full">
            {item.image ? (
              <img
                src={item.image || "/placeholder.svg"}
                alt={item.label}
                className={cn(
                  "object-contain select-none pointer-events-none transition-all duration-200",
                  isSelected ? "w-14 h-14" : "w-10 h-10",
                )}
                draggable={false}
                loading="lazy"
              />
            ) : (
              <span
                className={cn(
                  "font-bold select-none tabular-nums transition-all duration-200",
                  isSelected ? "text-2xl text-foreground" : "text-base text-muted-foreground",
                )}
              >
                {item.displayText}
              </span>
            )}
            {item.displayText && item.image && (
              <div
                className={cn(
                  "absolute -bottom-0.5 text-[9px] font-semibold select-none tabular-nums transition-colors duration-200",
                  isSelected ? "text-foreground" : "text-muted-foreground/70",
                )}
              >
                {item.displayText}
              </div>
            )}
          </div>
        </div>
      )
    })
  }, [data, scrollOffset, selectedIndex, isDragging, handleItemClick])

  return (
    <div className="flex flex-col items-center w-28">
      {label && (
        <div className="text-muted-foreground text-[10px] uppercase tracking-widest mb-2 font-semibold select-none">
          {label}
        </div>
      )}

      <div className="flex items-center gap-0.5">
        <button
          onClick={handlePrevious}
          disabled={selectedIndex === 0}
          aria-label="Previous item"
          tabIndex={-1}
          className={cn(
            "flex items-center justify-center w-5 h-5 rounded-full transition-all duration-150",
            selectedIndex === 0
              ? "text-muted-foreground/15 cursor-not-allowed"
              : "text-muted-foreground/60 hover:text-foreground hover:bg-accent active:scale-90",
          )}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="8"
            height="8"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="rotate-180"
          >
            <path d="M12 17.414 3.293 8.707l1.414-1.414L12 14.586l7.293-7.293 1.414 1.414L12 17.414z" />
          </svg>
        </button>

        <div
          ref={containerRef}
          className="relative select-none cursor-grab active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded-xl touch-none"
          style={{ width: 72, height: VISIBLE_HEIGHT }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onWheel={handleWheel}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="listbox"
          aria-label={label || "Scrollable selector"}
          aria-activedescendant={data[selectedIndex]?.id}
        >
          <div
            className="absolute left-0 right-0 rounded-lg bg-accent/80 pointer-events-none border border-border/30 shadow-sm"
            style={{
              top: centerOffset,
              height: ITEM_HEIGHT,
            }}
          />

          <div className="absolute inset-0 overflow-hidden rounded-xl">
            <div
              className="flex flex-col items-center will-change-transform"
              style={{
                transform: `translateY(${centerOffset - scrollOffset}px)`,
                transition: isDragging ? "none" : `transform ${TRANSITION_DURATION}ms cubic-bezier(0.33, 1, 0.68, 1)`,
              }}
            >
              {renderedItems}
            </div>
          </div>

          <div className="absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-popover via-popover/60 to-transparent pointer-events-none rounded-t-xl" />
          <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-popover via-popover/60 to-transparent pointer-events-none rounded-b-xl" />
        </div>

        <button
          onClick={handleNext}
          disabled={selectedIndex === data.length - 1}
          aria-label="Next item"
          tabIndex={-1}
          className={cn(
            "flex items-center justify-center w-5 h-5 rounded-full transition-all duration-150",
            selectedIndex === data.length - 1
              ? "text-muted-foreground/15 cursor-not-allowed"
              : "text-muted-foreground/60 hover:text-foreground hover:bg-accent active:scale-90",
          )}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 17.414 3.293 8.707l1.414-1.414L12 14.586l7.293-7.293 1.414 1.414L12 17.414z" />
          </svg>
        </button>
      </div>

      <div
        className="mt-2.5 text-[11px] font-medium text-foreground whitespace-nowrap overflow-hidden text-ellipsis w-full text-center select-none"
        title={data[selectedIndex].label}
      >
        {data[selectedIndex].label}
      </div>
    </div>
  )
}

export default function CameraControl({ value, onApply }: CameraControlProps) {
  const [presets, setPresets] = useState<CameraPreset[]>(DEFAULT_PRESETS)
  // null means "no preset selected"
  const [presetIndex, setPresetIndex] = useState<number | null>(null)
  const [cameraIndex, setCameraIndex] = useState(0)
  const [lensIndex, setLensIndex] = useState(4)
  const [focalLengthIndex, setFocalLengthIndex] = useState(2)
  const [apertureIndex, setApertureIndex] = useState(0)

  // Load presets from localStorage (and seed defaults if missing)
  useEffect(() => {
    try {
      const existing = safeParsePresets(window.localStorage.getItem(CAMERA_PRESETS_STORAGE_KEY))
      if (existing) {
        setPresets(existing)
        return
      }
      window.localStorage.setItem(CAMERA_PRESETS_STORAGE_KEY, JSON.stringify(DEFAULT_PRESETS))
      setPresets(DEFAULT_PRESETS)
    } catch {
      setPresets(DEFAULT_PRESETS)
    }
  }, [])

  // Sync from persisted selection (by id)
  useEffect(() => {
    const findIndex = (data: ScrollableItem[], id: string | undefined, fallback = 0) => {
      if (!id) return fallback
      const idx = data.findIndex((x) => x.id === id)
      return idx >= 0 ? idx : fallback
    }
    setCameraIndex((prev) => findIndex(cameraData, value?.cameraId, prev))
    setLensIndex((prev) => findIndex(lensData, value?.lensId, prev))
    setFocalLengthIndex((prev) => findIndex(focalLengthData, value?.focalId, prev))
    setApertureIndex((prev) => findIndex(apertureData, value?.apertureId, prev))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.cameraId, value?.lensId, value?.focalId, value?.apertureId])

  // Auto-detect preset only when none is selected:
  // if current node selection matches one preset exactly, highlight it.
  useEffect(() => {
    if (presetIndex !== null) return
    const sel: CameraControlSelection | null = (() => {
      const cameraId = value?.cameraId
      const lensId = value?.lensId
      const focalId = value?.focalId
      const apertureId = value?.apertureId
      if (!cameraId || !lensId || !focalId || !apertureId) return null
      return { cameraId, lensId, focalId, apertureId }
    })()
    if (!sel) return
    const idx = presets.findIndex((p) => {
      const s = p.selection
      return (
        s.cameraId === sel.cameraId &&
        s.lensId === sel.lensId &&
        s.focalId === sel.focalId &&
        s.apertureId === sel.apertureId
      )
    })
    if (idx >= 0) setPresetIndex(idx)
  }, [presets, presetIndex, value?.cameraId, value?.lensId, value?.focalId, value?.apertureId])

  const applySelectionToUi = useCallback((sel: CameraControlSelection) => {
    const findIndex = (data: ScrollableItem[], id: string, fallback = 0) => {
      const idx = data.findIndex((x) => x.id === id)
      return idx >= 0 ? idx : fallback
    }
    setCameraIndex((prev) => findIndex(cameraData, sel.cameraId, prev))
    setLensIndex((prev) => findIndex(lensData, sel.lensId, prev))
    setFocalLengthIndex((prev) => findIndex(focalLengthData, sel.focalId, prev))
    setApertureIndex((prev) => findIndex(apertureData, sel.apertureId, prev))
  }, [])

  const getLabelById = useCallback((data: ScrollableItem[], id: string) => {
    const found = data.find((x) => x.id === id)
    return found?.label ?? id
  }, [])

  const handleApply = useCallback(() => {
    const camera = cameraData[cameraIndex]
    const lens = lensData[lensIndex]
    const focal = focalLengthData[focalLengthIndex]
    const aperture = apertureData[apertureIndex]

    const selection: CameraControlSelection = {
      cameraId: camera?.id ?? cameraData[0].id,
      lensId: lens?.id ?? lensData[0].id,
      focalId: focal?.id ?? focalLengthData[0].id,
      apertureId: aperture?.id ?? apertureData[0].id,
    }

    onApply?.(buildCameraControlApplyPayloadFromSelection(selection))
  }, [cameraIndex, lensIndex, focalLengthIndex, apertureIndex, onApply])

  const handleSavePreset = useCallback(() => {
    const camera = cameraData[cameraIndex]
    const lens = lensData[lensIndex]
    const focal = focalLengthData[focalLengthIndex]
    const aperture = apertureData[apertureIndex]
    const selection: CameraControlSelection = {
      cameraId: camera?.id ?? cameraData[0].id,
      lensId: lens?.id ?? lensData[0].id,
      focalId: focal?.id ?? focalLengthData[0].id,
      apertureId: aperture?.id ?? apertureData[0].id,
    }
    const targetIndex = presetIndex ?? 0
    const next = presets.map((p, idx) => {
      if (idx !== targetIndex) return p
      return {
        ...p,
        selection,
        updatedAt: Date.now(),
      }
    })
    setPresets(next)
    if (presetIndex === null) setPresetIndex(targetIndex)
    try {
      window.localStorage.setItem(CAMERA_PRESETS_STORAGE_KEY, JSON.stringify(next))
    } catch {}
  }, [cameraIndex, lensIndex, focalLengthIndex, apertureIndex, presets, presetIndex])

  return (
    <div
      className="bg-popover/98 backdrop-blur-xl rounded-2xl border border-border/40 p-5 shadow-2xl shadow-black/10"
      onKeyDownCapture={(e) => {
        // Allow "Enter" to quickly apply from anywhere inside the panel (selectors use keyboard focus).
        if (e.key === "Enter") {
          e.preventDefault()
          e.stopPropagation()
          handleApply()
        }
      }}
    >
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-border/30">
        <span className="text-foreground text-sm font-semibold tracking-tight">摄像机控制</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSavePreset}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-accent text-foreground hover:bg-accent/80 active:scale-[0.97] transition-all duration-150 shadow-sm"
          >
            保存为预设
          </button>

          <button
            type="button"
            onClick={handleApply}
            className="px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.97] transition-all duration-150 shadow-sm"
          >
            应用
          </button>
        </div>
      </div>

      <div className="flex items-start justify-center gap-2">
        <ScrollableSelector
          data={cameraData}
          selectedIndex={cameraIndex}
          onSelectedIndexChange={setCameraIndex}
          label="机身"
        />

        <div className="w-px h-48 bg-border/30 self-center mt-4" />

        <ScrollableSelector
          data={lensData}
          selectedIndex={lensIndex}
          onSelectedIndexChange={setLensIndex}
          label="镜头"
        />

        <div className="w-px h-48 bg-border/30 self-center mt-4" />

        <ScrollableSelector
          data={focalLengthData}
          selectedIndex={focalLengthIndex}
          onSelectedIndexChange={setFocalLengthIndex}
          label="焦距"
        />

        <div className="w-px h-48 bg-border/30 self-center mt-4" />

        <ScrollableSelector
          data={apertureData}
          selectedIndex={apertureIndex}
          onSelectedIndexChange={setApertureIndex}
          label="光圈"
        />
      </div>

      {/* Presets (bottom) */}
      <div className="mt-5 pt-4 border-t border-border/30">
        <div className="text-muted-foreground text-[10px] uppercase tracking-widest mb-2 font-semibold select-none">
          预设
        </div>
        <div className="grid grid-cols-4 gap-2">
          {presets.map((p, idx) => {
            const selected = presetIndex !== null && idx === presetIndex
            const cameraLabel = getLabelById(cameraData, p.selection.cameraId)
            const lensLabel = getLabelById(lensData, p.selection.lensId)
            const focalLabel = getLabelById(focalLengthData, p.selection.focalId)
            const apertureLabel = getLabelById(apertureData, p.selection.apertureId)
            return (
              <button
                key={`${p.name}-${idx}`}
                type="button"
                className={cn(
                  "rounded-xl border px-3 py-2 text-left transition-colors",
                  selected
                    ? "border-primary/60 bg-primary/10"
                    : "border-border/50 hover:bg-accent/60",
                )}
                onClick={() => {
                  setPresetIndex(idx)
                  applySelectionToUi(p.selection)
                }}
              >
                <div className={cn("text-xs font-semibold", selected ? "text-primary" : "text-foreground")}>
                  {p.name}
                </div>
                <div className="mt-1 text-[10px] text-muted-foreground space-y-0.5">
                  <div className="grid grid-cols-[auto_1fr] gap-x-1 items-baseline" title={cameraLabel}>
                    <span className="text-muted-foreground/80">机身</span>
                    <span className="truncate">{cameraLabel}</span>
                  </div>
                  <div className="grid grid-cols-[auto_1fr] gap-x-1 items-baseline" title={lensLabel}>
                    <span className="text-muted-foreground/80">镜头</span>
                    <span className="truncate">{lensLabel}</span>
                  </div>
                  <div className="grid grid-cols-[auto_1fr] gap-x-1 items-baseline" title={focalLabel}>
                    <span className="text-muted-foreground/80">焦距</span>
                    <span className="truncate">{focalLabel}</span>
                  </div>
                  <div className="grid grid-cols-[auto_1fr] gap-x-1 items-baseline" title={apertureLabel}>
                    <span className="text-muted-foreground/80">光圈</span>
                    <span className="truncate">{apertureLabel}</span>
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}


